import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { format, parseISO } from 'date-fns';
import { useState, type FC, type MouseEvent } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';

import 'react-day-picker/style.css';
import { MdOutlineCalendarToday } from 'react-icons/md';

import { FLUID_TYPE } from '../fluid-type';
import { TAG_PICKER_PAPER_SX } from '../tag-picker-panel';
import { FILTER_ROSE, FILTER_ROSE_BG, FilterTriggerButton } from './filter-trigger-button';

type PanelProps = {
  /** YYYY-MM-DD */
  from?: string;
  /** YYYY-MM-DD (inclusive) */
  to?: string;
  onChange: (range: { from: string | undefined; to: string | undefined }) => void;
  /** Called after Apply/Clear — the popover host closes, the drawer host collapses. */
  onDone?: () => void;
  /** The drawer's section card already labels the panel — hide the title row. */
  hideTitle?: boolean;
  /** Smaller day cells so the calendar + apply footer fit a phone-height
      drawer section without the actions falling below the fold. */
  compact?: boolean;
};

type Props = Omit<PanelProps, 'onDone' | 'hideTitle' | 'compact'>;

// The community opened on 2026-05-01 — there are no posts before then, so the
// calendar can't select or navigate earlier than this. (Month is 0-indexed.)
const COMMUNITY_OPEN_DATE = new Date(2026, 4, 1);

const toDisplay = (date: string) => format(parseISO(date), 'MMM d, yyyy');

export const formatDateRangeValue = (from?: string, to?: string): string | undefined => {
  if (from && to) {
    return `${toDisplay(from)} – ${toDisplay(to)}`;
  }
  if (from) {
    return `From ${toDisplay(from)}`;
  }
  if (to) {
    return `Until ${toDisplay(to)}`;
  }
  return undefined;
};

/** Footer summary of the in-panel (not yet applied) selection. */
const formatPending = (pending?: DateRange): string => {
  if (!pending?.from) {
    return 'Pick a start date';
  }
  const fromLabel = format(pending.from, 'MMM d, yyyy');
  return pending.to ? `${fromLabel} – ${format(pending.to, 'MMM d, yyyy')}` : `${fromLabel} – …`;
};

/**
 * The calendar + apply footer, shared by the desktop popover and the mobile
 * filter drawer. Pending state is seeded from the applied range on mount —
 * both hosts unmount the panel when it closes, so each open starts fresh.
 */
export const DateRangePanel: FC<PanelProps> = ({ from, to, onChange, onDone, hideTitle = false, compact = false }) => {
  const [pending, setPending] = useState<DateRange | undefined>(() => ({
    from: from ? parseISO(from) : undefined,
    to: to ? parseISO(to) : undefined,
  }));

  const handleApply = () => {
    onChange({
      from: pending?.from ? format(pending.from, 'yyyy-MM-dd') : undefined,
      to: pending?.to ? format(pending.to, 'yyyy-MM-dd') : undefined,
    });
    onDone?.();
  };

  const handleClear = () => {
    setPending(undefined);
    onChange({ from: undefined, to: undefined });
    onDone?.();
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {!hideTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 0.5 }}>
          <Typography
            sx={{ fontFamily: 'Figtree, sans-serif', fontSize: FLUID_TYPE.label, fontWeight: 700, color: '#333' }}
          >
            Filter by date
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          px: 0.5,
          '& .rdp-root': {
            '--rdp-accent-color': FILTER_ROSE,
            '--rdp-accent-background-color': FILTER_ROSE_BG,
            fontFamily: 'Figtree, sans-serif',
            fontSize: FLUID_TYPE.label,
            // Compact hosts (the mobile drawer): the month grid stretches to
            // the card's full width — day cells share it evenly — and rows are
            // shorter so the calendar + footer fit without hiding the buttons.
            ...(compact && {
              width: '100%',
              '--rdp-day-width': 'auto',
              '--rdp-day-height': '34px',
              '--rdp-day_button-width': '34px',
              '--rdp-day_button-height': '34px',
              '--rdp-nav_button-width': '28px',
              '--rdp-nav_button-height': '28px',
              '& .rdp-months': { width: '100%', maxWidth: 'none' },
              '& .rdp-month': { width: '100%' },
              '& .rdp-month_grid': { width: '100%' },
              '& .rdp-day_button': { mx: 'auto' },
            }),
          },
        }}
      >
        <DayPicker
          mode="range"
          selected={pending}
          onSelect={setPending}
          // No posts exist before the community opened or after today, so both
          // ends are out of range — and month navigation is capped to the same
          // window so you can't page past either bound.
          startMonth={COMMUNITY_OPEN_DATE}
          endMonth={new Date()}
          disabled={{ before: COMMUNITY_OPEN_DATE, after: new Date() }}
        />
      </Box>
      {/* In compact hosts the footer stacks — the summary + two pills don't
          fit one row in the drawer card's width — and sticks to the bottom of
          the card's capped scroll area so Clear/Apply are always in reach. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          pt: '12px',
          mt: 0.5,
          borderTop: '1px solid #f0f0f0',
          px: 0.5,
          ...(compact && {
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            bgcolor: 'common.white',
            mx: '-12px',
            px: '16px',
            pb: '10px',
            mb: '-12px',
          }),
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontSize: FLUID_TYPE.caption,
            color: '#808080',
            ...(compact && { flexBasis: '100%' }),
          }}
        >
          {formatPending(pending)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(compact && { width: '100%' }) }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleClear}
            disabled={!(from || to || pending?.from)}
            sx={{
              borderRadius: '10px',
              height: 34,
              minHeight: 34,
              textTransform: 'none',
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 600,
              px: 3,
              boxShadow: 'none',
              ...(compact && { flex: 1 }),
              '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
                background: theme => theme.palette.primary.main,
                color: theme => theme.palette.primary.contrastText,
                '&:hover': { background: theme => theme.palette.primary.dark, boxShadow: 'none' },
              },
              '&&.Mui-disabled': { background: '#E2E2E2 !important', color: '#9E9E9E' },
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            disabled={!pending?.from}
            sx={{
              borderRadius: '10px',
              height: 34,
              minHeight: 34,
              textTransform: 'none',
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 600,
              px: 3,
              boxShadow: 'none',
              ...(compact && { flex: 1 }),
              '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
                background: theme => theme.palette.primary.main,
                color: theme => theme.palette.primary.contrastText,
                '&:hover': { background: theme => theme.palette.primary.dark, boxShadow: 'none' },
              },
              '&&.Mui-disabled': { background: '#E2E2E2 !important', color: '#9E9E9E' },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Date range filter — from / to picker in a popover, filtering posts by
 * creation date (both ends inclusive). Styled to match the other filter
 * panels: header with a clear action, live range summary and a pill apply
 * button.
 */
export const DateRangeFilter: FC<Props> = ({ from, to, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <FilterTriggerButton
        label="Date range"
        icon={<MdOutlineCalendarToday size={15} />}
        value={formatDateRangeValue(from, to)}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
      />
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { ...TAG_PICKER_PAPER_SX, width: 'auto' } } }}
      >
        <DateRangePanel from={from} to={to} onChange={onChange} onDone={() => setAnchorEl(null)} />
      </Popover>
    </>
  );
};
